import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import { useNavigateWithBreadcrumbs } from '../../hooks';
import { ROUTES } from '../../constants';
import i18n from '../../utilities/i18n';
import SubjectPageHeader from './subjectComponents/SubjectHeader';
import { Device, Sensor } from '../../models';
import {
  Button,
  Highlight,
  IconBannersWarning,
  LinkText,
  LoadingSpinner,
  Table,
  VStack,
} from '@adc/parallax-component-library';
import DynamicTable, { ColumnConfig } from '../table/DynamicTable';
import DateCellRenderer from '../table/DateCellRenderer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSubjectNoteMutateOptions,
  devicesBySubjectQueryOptions,
  sensorsQueryOptions,
  siteQueryOptions,
  studyQueryOptions,
  subjectQueryOptions,
} from '../../queryOptions';
import { UploadHistoryModal } from '../modal/UploadHistoryModal';
import CreateSubjectNoteModal from '../modal/CreateSubjectNoteModal';
import { EntityType, UserNote } from 'adc-sirius-module-common';
import { AssignDeviceModal } from '../deviceAssignment/AssignDeviceModal';
import { useErrorMessageOnEntityView } from '../../hooks/useErrorMessageOnEntityView';
import { QrCodePopup } from '../qrCode/QrCodePopup';

/**
 * Subject component that displays the details of a specific subject.
 * @returns {React.JSX.Element}
 */
export default function Subject() {
  const queryClient = useQueryClient();

  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const navigateToSensor = (sensorId: string, sensorSerialNumber: string) => {
    navigateWithBreadcrumbs(
      ROUTES.SENSORS.DETAIL.replace(':sensorKey', sensorId),
      sensorSerialNumber,
      subject.data?.name || i18n.t('Page_Subject.title'),
    );
  };

  const { subjectKey } = useParams();

  const subject = useQuery(subjectQueryOptions({ key: subjectKey }));

  const errorMessage = useErrorMessageOnEntityView(
    subject.error,
    'Page_Subject.load_error',
  );

  const site = useQuery(
    siteQueryOptions(
      { key: subject.data?.siteKey },
      { enabled: !!subject.data?.siteKey },
    ),
  );
  const study = useQuery(
    studyQueryOptions(
      { key: site.data?.studyKey },
      { enabled: !!site.data?.studyKey },
    ),
  );
  const sensors = useQuery(
    sensorsQueryOptions({ subjectKey }, { enabled: !!subjectKey }),
  );

  const devices = useQuery(
    devicesBySubjectQueryOptions(
      { key: subjectKey },
      { enabled: !!subjectKey },
    ),
  );

  const createNote = useMutation(
    createSubjectNoteMutateOptions({
      onSuccess: (updatedSubject) => {
        // update only the notes in the cached subject data
        queryClient.setQueryData(
          subjectQueryOptions({ key: subjectKey }).queryKey,
          (cachedSubject) => {
            if (!cachedSubject) {
              return updatedSubject;
            }

            return {
              ...cachedSubject,
              userNotes: updatedSubject.userNotes || cachedSubject.userNotes,
              updatedOn: updatedSubject.updatedOn || cachedSubject.updatedOn,
              updatedBy: updatedSubject.updatedBy || cachedSubject.updatedBy,
              updateReason:
                updatedSubject.updateReason || cachedSubject.updateReason,
            };
          },
        );
      },
    }),
  );

  const devicesTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Page_Subject.device_name'), dataKey: 'appName' },
    { label: i18n.t('Page_Subject.device_id'), dataKey: 'deviceId' },
    { label: i18n.t('Page_Subject.device_model'), dataKey: 'phoneModel' },
    {
      label: i18n.t('Page_Subject.device_app_version'),
      dataKey: 'bundleVersion',
    },
    { label: i18n.t('Entity.createdOn'), dataKey: 'createdOn' },
    { label: '', dataKey: 'uploadHistory', isSortable: false },
  ];

  const sensorsTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Entity.serialNumber'), dataKey: 'serialNumber' },
    { label: i18n.t('Entity.condition'), dataKey: 'conditionCode' },
    {
      label: i18n.t('Entity.lifecount'),
      dataKey: 'lifecount',
      getSortValue: (sensor: Sensor) => sensor.analyteCounts[0]?.current ?? 0,
    },
    { label: i18n.t('Entity.activatedOn'), dataKey: 'activationTime' },
  ];

  const notesTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Entity.timestamp'), dataKey: 'timestamp' },
    { label: i18n.t('Entity.note'), dataKey: 'note' },
    { label: i18n.t('Entity.createdBy'), dataKey: 'user_display_name' },
  ];

  const devicesTableCellRenderer = (dataKey: string, device: Device) => {
    if (dataKey === 'createdOn') {
      return <DateCellRenderer value={device.createdOn} key={dataKey} />;
    } else if (dataKey === 'uploadHistory') {
      return (
        <Table.TextCell key={dataKey}>
          <UploadHistoryModal
            deviceId={device.key || ''}
            deviceName={device.appName || ''}
            trigger={
              <LinkText onPress={() => {}} type="$bodySmall.default">
                {i18n.t('Page_Subject.upload_history')}
              </LinkText>
            }
          />
        </Table.TextCell>
      );
    } else {
      return <Table.TextCell key={dataKey}>{device[dataKey]}</Table.TextCell>;
    }
  };

  const sensorsTableCellRenderer = (dataKey: string, sensor: Sensor) => {
    if (dataKey === 'lifecount') {
      return (
        <Table.TextCell key={dataKey}>
          {sensor.analyteCounts[0]?.current ?? 0}
        </Table.TextCell>
      );
    } else if (dataKey === 'activationTime') {
      return <DateCellRenderer value={sensor.activationTime} key={dataKey} />;
    } else if (dataKey === 'serialNumber') {
      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            onPress={() => navigateToSensor(sensor.key, sensor.serialNumber)}
            type="$bodySmall.default"
          >
            {sensor[dataKey]}
          </LinkText>
        </Table.TextCell>
      );
    } else {
      return <Table.TextCell key={dataKey}>{sensor[dataKey]}</Table.TextCell>;
    }
  };

  const notesTableCellRenderer = (dataKey: string, note: UserNote) => {
    if (dataKey === 'timestamp') {
      return <DateCellRenderer value={note.timestamp} key={dataKey} />;
    } else {
      return <Table.TextCell key={dataKey}>{note[dataKey]}</Table.TextCell>;
    }
  };

  const qrCodeError = useMemo(() => {
    if (
      study.error ||
      site.error ||
      (study.isSuccess && !study.data) ||
      (site.isSuccess && !site.data) ||
      (subject.isSuccess && !subject.data)
    ) {
      return true;
    }

    return false;
  }, [
    study.error,
    study.isSuccess,
    study.data,
    site.error,
    site.isSuccess,
    site.data,
    subject.isSuccess,
    subject.data,
  ]);

  return (
    <VStack gap={'$4'} height={'100%'}>
      {subject.isError ? (
        <Highlight
          variant="danger"
          iconStart={
            <IconBannersWarning
              color="$neutral.0"
              accentColor="$support.danger.default"
            />
          }
          title={i18n.t('Page_Subject.load_error_title')}
          body={i18n.t(errorMessage)}
          testID="subject-error-banner"
        />
      ) : null}

      {subject.isPending ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="subject-loading" />
        </VStack>
      ) : null}

      {subject.isSuccess ? (
        <VStack gap="$4">
          <SubjectPageHeader subject={subject.data}>
            <QrCodePopup
              studyIdentifier={study.data?.name || ''}
              siteIdentifier={site.data?.name}
              subjectIdentifier={subject.data.name}
              studyKey={study.data?.key || ''}
              siteKey={site.data?.key}
              subjectKey={subject.data.key}
              error={qrCodeError}
              loading={study.isPending || site.isPending}
            />
          </SubjectPageHeader>
          <DynamicTable
            isLoading={sensors.isPending}
            hasLoadingError={sensors.isError}
            title={i18n.t('Page_Subject.table_sensors_title')}
            description={i18n.t('Page_Subject.table_sensors_description')}
            columnData={sensorsTableColumnData}
            rowData={sensors.data || []}
            cellRenderer={sensorsTableCellRenderer}
          />
          <DynamicTable
            isLoading={devices.isPending}
            hasLoadingError={devices.isError}
            title={i18n.t('Page_Subject.table_devices_title')}
            description={i18n.t('Page_Subject.table_devices_description')}
            columnData={devicesTableColumnData}
            rowData={devices.data || []}
            cellRenderer={devicesTableCellRenderer}
            toolbarRightContent={
              <AssignDeviceModal
                entityKey={subject.data.key}
                entityName={subject.data.name}
                entityType={EntityType.StudySubject}
                trigger={
                  <Button
                    size="medium"
                    label={i18n.t('Page_Subject.assign_device')}
                    onPress={() => {}}
                    disabled={devices.isPending}
                    testID="assign-device-button"
                  />
                }
              />
            }
          />
          <DynamicTable
            isLoading={subject.isLoading}
            hasLoadingError={subject.isError}
            title={i18n.t('Page_Subject.table_notes_title')}
            description={i18n.t('Page_Subject.table_notes_description')}
            columnData={notesTableColumnData}
            rowData={subject.data.userNotes || []}
            cellRenderer={notesTableCellRenderer}
            toolbarRightContent={
              <CreateSubjectNoteModal
                trigger={
                  <Button
                    size="medium"
                    label={i18n.t('Page_Subject.add_note')}
                    onPress={() => {}}
                    disabled={createNote.isPending}
                    testID="add-note-button"
                  />
                }
                onSuccess={async (newNote) => {
                  await createNote.mutateAsync({
                    subjectKey: subject.data.key,
                    noteAddRequest: newNote,
                  });
                }}
              />
            }
          />
        </VStack>
      ) : null}
    </VStack>
  );
}
